import {
	ActivityActorType,
	ActivityKind,
	type AdminAssignment,
	type GetAdminActivityFeedQuery,
	useGetAdminActivityFeedQuery,
} from '@codezo/graphql';
import { ArrowRight, Check, CreditCard, MessageSquare, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCurrencyStore } from '../../stores/currency';
import { InitialsAvatar } from '../ui/initials-avatar';
import { money } from './performance/performanceFormat';

type Entry = GetAdminActivityFeedQuery['getAdminActivityFeed']['entries'][number];

const POLL_MS = 60_000;
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

function groupLabel(d: Date, now: Date): string {
	const days = Math.round((startOfDay(now) - startOfDay(d)) / 86_400_000);
	if (days === 0) return 'Today';
	if (days === 1) return 'Yesterday';
	return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' });
}

/** "12 min", "3 h" today; the clock time on earlier days, which have their own heading. */
function timeLabel(d: Date, now: Date): string {
	if (startOfDay(d) === startOfDay(now)) {
		const min = Math.max(0, Math.round((now.getTime() - d.getTime()) / 60_000));
		return min < 60 ? `${min} min` : `${Math.floor(min / 60)} h`;
	}
	return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

/** An admin shows as their initials; everyone else as what happened. */
function Mark({ e }: { e: Entry }) {
	if (e.actorType === ActivityActorType.Admin && e.actorName) {
		return <InitialsAvatar name={e.actorName} size={22} />;
	}
	const icon =
		e.kind === ActivityKind.Message ? (
			<MessageSquare size={12} />
		) : e.kind === ActivityKind.Accepted ? (
			<Check size={12} />
		) : e.kind === ActivityKind.Payment ? (
			<CreditCard size={12} />
		) : e.kind === ActivityKind.Declined ? (
			<X size={12} />
		) : (
			<ArrowRight size={12} />
		);
	const good = e.kind === ActivityKind.Accepted || e.kind === ActivityKind.Payment;
	return (
		<span
			className={
				good
					? 'flex size-[22px] items-center justify-center rounded-full bg-status-success-soft text-status-success'
					: 'flex size-[22px] items-center justify-center rounded-full bg-surface-fill text-text-2'
			}
		>
			{icon}
		</span>
	);
}

function Row({ e, currency, now }: { e: Entry; currency: string; now: Date }) {
	const navigate = useNavigate();
	const client = e.organization?.name ?? 'A client';
	// Admins by first name in the sentence, as people say it; the avatar takes the full name.
	const who =
		e.actorType === ActivityActorType.Client
			? client
			: e.actorType === ActivityActorType.Admin
				? (e.actorName?.split(' ')[0] ?? 'An admin')
				: (e.actorName ?? '05');
	const target = e.orderId
		? `/orders/${e.orderId}`
		: e.orderQuoteId
			? `/quotes/${e.orderQuoteId}`
			: null;
	return (
		<div
			className="grid h-11 cursor-pointer grid-cols-[22px_minmax(0,1fr)_auto_52px] items-center gap-3 border-b border-line-1 px-4 text-base transition-colors last:border-b-0 hover:bg-surface-hover"
			onClick={() => target && navigate(target)}
			role="link"
			tabIndex={0}
			onKeyDown={(ev) => ev.key === 'Enter' && target && navigate(target)}
			title={e.detail ?? undefined}
		>
			<Mark e={e} />
			<span className="truncate text-text-3">
				<span className="font-medium text-text-1">{who}</span> {e.verb}{' '}
				{e.objectIsClient && <span className="text-text-1">{client}</span>}
				{!e.objectIsClient && e.object && <span className="text-text-1">{e.object}</span>}
				{e.amount != null && (
					<span className="text-text-1 tabular-nums">{money(e.amount, currency)}</span>
				)}
			</span>
			<span className="hidden max-w-[160px] truncate font-mono text-xs text-text-4 sm:block">
				{e.ref}
			</span>
			<span className="text-right text-xs text-text-4 tabular-nums">
				{timeLabel(new Date(e.at), now)}
			</span>
		</div>
	);
}

/**
 * What just happened across the studio: offers out and answered, money in, production and samples
 * moving, supplier replies. Grouped by day, newest first.
 */
export function ActivityFeed({ assignment }: { assignment: AdminAssignment }) {
	const { selectedCurrency } = useCurrencyStore();
	const { data, loading, error, refetch } = useGetAdminActivityFeedQuery({
		variables: { assignment, limit: 14, currency: selectedCurrency },
		fetchPolicy: 'no-cache',
		pollInterval: POLL_MS,
	});
	const feed = data?.getAdminActivityFeed;
	const now = new Date();
	const groups = (feed?.entries ?? []).reduce<{ label: string; entries: Entry[] }[]>((acc, e) => {
		const label = groupLabel(new Date(e.at), now);
		const last = acc[acc.length - 1];
		if (last && last.label === label) last.entries.push(e);
		else acc.push({ label, entries: [e] });
		return acc;
	}, []);

	return (
		<section
			className="overflow-hidden rounded-xl border border-line-1 bg-surface"
			aria-labelledby="act-h"
		>
			<div className="flex h-11 items-center border-b border-line-1 px-4">
				<h2
					id="act-h"
					className="text-sm font-medium uppercase tracking-[0.04em] text-text-1"
				>
					Activity
				</h2>
				<span className="ml-2 text-sm text-text-4">last two weeks</span>
			</div>
			{loading && !feed && (
				<div className="flex flex-col gap-3 p-4">
					{[0, 1, 2, 3].map((i) => (
						<span key={i} className="h-4 w-2/3 animate-pulse rounded bg-surface-fill" />
					))}
				</div>
			)}
			{error && !feed && (
				<div className="flex items-center justify-center gap-3 px-4 py-10 text-sm text-text-3">
					Couldn’t load activity.
					<button
						type="button"
						onClick={() => refetch()}
						className="text-text-1 hover:text-accent"
					>
						Retry
					</button>
				</div>
			)}
			{feed && groups.length === 0 && (
				<div className="px-4 py-10 text-center text-sm text-text-3">
					Nothing has happened in the last two weeks.
				</div>
			)}
			{groups.map((g) => (
				<div key={g.label}>
					<div className="flex h-8 items-center border-b border-line-1 bg-surface-hover px-4 text-xs text-text-3">
						{g.label}
					</div>
					{g.entries.map((e) => (
						<Row key={e.id} e={e} currency={feed!.currency} now={now} />
					))}
				</div>
			))}
		</section>
	);
}
